import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { firstValueFrom, Subject } from "rxjs";
import { ErrorMessageComponent } from "src/app/components/global/snackbar/impl/error-message/error-message.component";
import { SnackbarService } from "src/app/components/global/snackbar/snackbar.service";

export interface HttpObserve<T = unknown> {
  headers: HttpHeaders;
  body: T;
}

@Injectable({
  providedIn: "root",
})
export class HttpService {
  private http: HttpClient;
  private snackbar: SnackbarService;
  private router: Router;
  public retry: Subject<void>;

  public constructor() {
    this.http = inject(HttpClient);
    this.snackbar = inject(SnackbarService);
    this.router = inject(Router);
    this.retry = new Subject();
  }

  /**
   * Retrieve data from url
   * @param url
   * @param data
   * @returns
   */
  public async retrieve<T = unknown>(
    url: string,
    data?: unknown,
    options: Parameters<typeof this.http.get>[1] & { bypassErrorHandling?: boolean; retry?: boolean } = {},
  ): Promise<T> {
    try {
      const response = await firstValueFrom(
        this.http.get<T>(`${url}?${new URLSearchParams(<Record<string, string>>data).toString()}`, {
          ...options,
          observe: "response",
        }),
      );

      if (response.body) {
        return response.body;
      } else {
        throw new Error(`Invalid response from [retrieve=${url}]`);
      }
    } catch (error) {
      if (options.retry) {
        await firstValueFrom(this.retry);
        console.log("Retrying request (retrieve)..", url, data, options);
        return this.retrieve(url, data, options);
      } else {
        if (!options.bypassErrorHandling) this.onError(error);
        throw error;
      }
    }
  }

  /**
   * Send data to url
   * @param url
   * @param data
   * @returns
   */
  public async sendObserve<T = unknown>(url: string, data: unknown, options: Parameters<typeof this.http.post>[2] = {}): Promise<HttpResponse<T>> {
    try {
      const response = await firstValueFrom(
        this.http.post<HttpResponse<T>>(url, data, {
          ...options,
          observe: "response",
        }),
      );

      return response;
    } catch (error) {
      this.onError(error);
      throw error;
    }
  }

  /**
   * Send data to url
   * @param url
   * @param data
   * @returns
   */
  public async send<T = unknown>(
    url: string,
    data: unknown,
    options: Parameters<typeof this.http.post>[2] & { bypassErrorHandling?: boolean; retry?: boolean; responseHeadersOut?: Headers } = {},
  ): Promise<T> {
    try {
      const response = await firstValueFrom(
        this.http.post<HttpResponse<T>>(url, data, {
          ...options,
          observe: "response",
        }),
      );

      if (options.responseHeadersOut != undefined) {
        for (const key of response.headers.keys()) {
          const value = response.headers.get(key);
          if (value) options.responseHeadersOut.set(key, value);
        }
      }

      if (response.body) {
        return response.body;
      } else {
        throw new Error(`Invalid response from [send=${url}]`);
      }
    } catch (error) {
      if (options.retry) {
        await firstValueFrom(this.retry);
        console.log("Retrying request (send)..", url, data, options);
        return this.send(url, data, options);
      } else {
        if (!options.bypassErrorHandling) this.onError(error);
        throw error;
      }
    }
  }

  /**
   * send formdata to url
   * @param url
   * @param data
   * @param headers
   * @returns
   */
  public async sendForm<T = unknown>(url: string, data: Record<string, unknown>, headers = {}): Promise<T> {
    const formdata = new FormData();
    for (const [key, value] of Object.entries(data)) formdata.set(key, <string | Blob>value);
    try {
      const response = await firstValueFrom(
        this.http.post<T>(url, formdata, {
          headers,
          observe: "response",
        }),
      );

      if (response.body) {
        return response.body;
      } else {
        throw new Error(`Invalid response from [sendForm=${url}]`);
      }
    } catch (error) {
      this.onError(error);
      throw error;
    }
  }

  private onError(error: unknown): void {
    if (error instanceof HttpErrorResponse) {
      console.log("Handling status for", error.url, error.status);
      this.handleStatus(error.status);
    } else {
      console.error("Failed to load result => ", error);
      this.snackbar.open<string>(ErrorMessageComponent, "ERRORS.RESPONSE");
    }
  }

  private handleStatus(status: number): void {
    switch (status) {
      case 401:
        this.onStatus401();
        break;

      case 500:
        this.onStatus500();
        break;

      default:
        console.error(`[HTTP] Unhandled status code: ${status}`);
        break;
    }
  }

  private onStatus401(): void {
    if (this.router.url.startsWith("/app")) this.router.navigate(["/app/home/"]);
    this.snackbar.open<string>(ErrorMessageComponent, "ERRORS.UNAUTHORIZED");
  }

  private onStatus500(): void {
    this.onError("[HTTP] Fatal Server Error");
    this.router.navigate(["/app/home/"]);
  }
}
